import React, { useState, useEffect, useRef } from 'react'
import styles from './styles.module.css'
import { toSrc } from '../../../../utils/imageSrc'

const cx = (...c) => c.filter(Boolean).join(' ')

function AccessoryImageWithTooltip({ src, alt, galleryImages }) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isHovering, setIsHovering] = useState(false)
  const preloadedRef = useRef(false)

  // Use gallery images if available, otherwise fall back to single src.
  // Post-#67 normalizeOffers, both `galleryImages` entries and the accessory
  // `src` (item.imageUrl) are ResolvedImage descriptor OBJECTS, not URL strings.
  // Collapse each to a bare `{ src: <string> }` here (this component reads
  // `.src` straight into <img src>), so a descriptor never stringifies to
  // "[object Object]". toSrc is a no-op for any legacy string value.
  const images = galleryImages?.length > 0
    ? galleryImages.map((img) => ({ src: toSrc(img) })).filter((img) => img.src)
    : (toSrc(src) ? [{ src: toSrc(src) }] : [])

  // Preload all images when hovering starts
  useEffect(() => {
    if (isHovering && !preloadedRef.current && images.length > 1) {
      preloadedRef.current = true
      images.forEach(img => {
        const preloadImg = new Image()
        preloadImg.src = img.src
      })
    }
  }, [isHovering, images])

  if (images.length === 0) return null

  const hasMultipleImages = images.length > 1
  const currentImage = images[currentIndex]

  const goToPrevious = (e) => {
    e.stopPropagation()
    setCurrentIndex(prev => (prev === 0 ? images.length - 1 : prev - 1))
  }

  const goToNext = (e) => {
    e.stopPropagation()
    setCurrentIndex(prev => (prev === images.length - 1 ? 0 : prev + 1))
  }

  const goToIndex = (e, index) => {
    e.stopPropagation()
    setCurrentIndex(index)
  }

  const handleMouseEnter = () => {
    setCurrentIndex(0)
    setIsHovering(true)
  }

  const handleMouseLeave = () => {
    setIsHovering(false)
  }

  return (
    <div className={styles.accessoryImageWrapper} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <img className={styles.accessoryImage} src={images[0].src} alt={alt} width={48} height={48}/>
      <div className={styles.accessoryImageTooltip}>
        <div className={styles.tooltipCarousel}>
          <div className={styles.imageContainer}>
            {hasMultipleImages && (
              <button className={cx(styles.navArrow, styles.navArrowLeft)} onClick={goToPrevious}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
              </button>
            )}
            <img className={styles.accessoryImageLarge} src={currentImage.src} alt={alt} width={200} height={200}/>
            {hasMultipleImages && (
              <button className={cx(styles.navArrow, styles.navArrowRight)} onClick={goToNext}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 6 15 12 9 18"/>
                </svg>
              </button>
            )}
          </div>
          {hasMultipleImages && (
            <div className={styles.dots}>
              {images.map((_, index) => (
                <button
                  key={index}
                  className={cx(styles.dot, index === currentIndex && styles.dotActive)}
                  onClick={(e) => goToIndex(e, index)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AccessoryImageWithTooltip
